import { Metaproduct, MetaproductOption } from '@scalefast/core';
import { GenericLogger } from '@scalefast/ecommerce-core';

export class ProductHelper {
  /**
   * Registry to track product IDs that have already triggered warnings.
   *
   * This set is primarily used in sorting methods: getSortedMetaproductsByColor and getSortedMetaproductsByLabel
   * to prevent duplicate warning messages for the same products across multiple
   * component lifecycle executions (ngOnInit, ngOnChanges).
   *
   * When products with missing color or label are detected during sorting,
   * their IDs are stored here to ensure each warning is logged only once.
   */
  private static warnedProductIds = new Set<string>();

  public static checkIfIsInFeeds(productFeeds: number[], configFeeds: number[]): boolean {
    const set = new Set(productFeeds);
    for (let num of configFeeds) {
      if (set.has(num)) {
        return true;
      }
    }
    return false;
  }

  public static processESChildren(child: any, colorReferences: Record<string, string> | undefined): any {
    // Structure for v4ApiResponse.metaproduct children
    const priceEntry = child.prices.find((price: any) => price.bundle_object_id === '-1');
    const price = priceEntry.price.toString();
    const crossedPrice =
      priceEntry.crossed_price > priceEntry.price && priceEntry.crossed_price > 0 ? priceEntry.crossed_price.toString() : '';
    return {
      label: child.edition,
      id: child.productID,
      hasStock: Number(child.stock) > 0,
      stockPolicy: child.ostock,
      price: price ? price : '',
      crossedPrice: crossedPrice ? crossedPrice : '',
      color: child.platform,
      hexcode: colorReferences?.[child.platform] || '',
    };
  }

  public static processV4Children(child: any): any {
    // Structure for specificV4 options
    const platformField = child.custom_fields.find((field: any) => field.type === 'CF_PLATFORM');
    const hexcodeField = child.custom_fields.find((field: any) => field.type === 'CF_HEXCODE');
    return {
      label: child.value,
      id: child.productID,
      hasStock: child.has_stock,
      stockPolicy: child.stock_policy,
      price: child.price.toString() || '',
      color: platformField ? platformField.value : '',
      hexcode: hexcodeField ? hexcodeField.value : '',
    };
  }

  public static getSortedMetaproductsByColor(metaproduct: Metaproduct, logger: GenericLogger): MetaproductOption[] {
    return ProductHelper.sortMetaproductsByField(metaproduct, logger, 'color');
  }

  public static getSortedMetaproductsByLabel(metaproduct: Metaproduct, logger: GenericLogger): MetaproductOption[] {
    return ProductHelper.sortMetaproductsByField(metaproduct, logger, 'label');
  }

  /**
   * Sorts an array of MetaproductOption objects by a specified field (color or label).
   * If the field is missing, a warning is logged.
   *
   * @param metaproductsOptions - Array of MetaproductOption objects to sort
   * @param logger - Logger instance
   * @param fieldName - The field to sort by: 'color' or 'label'
   * @returns A new array of MetaproductOption objects sorted by the specified field.
   */
  private static sortMetaproductsByField(
    metaproducts: Metaproduct,
    logger: GenericLogger,
    fieldName: 'color' | 'label',
  ): MetaproductOption[] {
    ProductHelper.logWhenMissingField(metaproducts, fieldName, logger);

    return metaproducts.options.sort((a, b) => {
      const textA = a[fieldName]?.toLowerCase() || '';
      const textB = b[fieldName]?.toLowerCase() || '';

      return textA.localeCompare(textB);
    });
  }

  /**
   * Logs a warning if any MetaproductOption objects are missing the specified field (color or label).
   * The product IDs are stored to prevent duplicate warnings.
   *
   * @param metaproductsOptions - Array of MetaproductOption objects to check
   * @param fieldName - The field to check for: 'color' or 'label'
   * @param logger - Logger instance
   */
  private static logWhenMissingField(metaproduct: Metaproduct, fieldName: 'color' | 'label', logger: GenericLogger): void {
    const metaproductsOptionsWithoutField = metaproduct.options.filter((option) => {
      // Check if the field is missing and if the product ID has not been warned before
      const uniqueKey = ProductHelper.getUniqueKey(metaproduct, option, fieldName);
      return !option[fieldName] && !ProductHelper.warnedProductIds.has(uniqueKey);
    });

    if (metaproductsOptionsWithoutField.length === 0) {
      return;
    }

    const missingFieldIds = metaproductsOptionsWithoutField
      .map((option) => {
        // Generate a unique key for the product to track warnings
        const uniqueKey = ProductHelper.getUniqueKey(metaproduct, option, fieldName);
        ProductHelper.warnedProductIds.add(uniqueKey);
        return option.id;
      })
      .join(', ');

    logger.warn({
      msg: `Found ${metaproductsOptionsWithoutField.length} metaproductOption without ${fieldName}. Parent ID: ${metaproduct.parentProduct?.id} Product IDs: [${missingFieldIds}]. It is possible that the product is not configured correctly in the merchant.`,
      details: {
        parentProduct: metaproduct.parentProduct,
        metaproductsOptionsWithoutField,
      },
    });
  }

  private static getUniqueKey(
    metaproduct: Metaproduct,
    metaproductOption: MetaproductOption,
    fieldName: 'color' | 'label',
  ): string {
    return [metaproduct.parentProduct?.id, metaproductOption.id, fieldName, metaproductOption[fieldName] ?? 'NoLabel']
      .filter(Boolean)
      .join('-');
  }
}
